import GeneralHelper from "../../utils/GeneralHelper";
import DateAndValue from "../DateAndValue";
import { DateTime } from "luxon";
import { ASCENDANT_ORDER, DESCENDANT_ORDER } from "../../config";

export default class WorkingCapital {
  constructor({
    dates,
    years,
    monthlyTotalCash = [],
    monthlyTotalAccountsReceivable = [],
    monthlyTotalSales = [],
    monthlyTotalAccountsPayable = [],
    monthlyTotalCostOfSales = [],
    monthlyTotalInventory = [],
    monthlyTotalOtherCurrentAssets = [],
    monthlyTotalOtherCurrentLiabilities = [],
    monthlyTotalMarketableSecurities = [],
    dateOrder = DESCENDANT_ORDER,
  }) {
    this.dates = dates;
    this.years = years;
    this.monthlyTotalCash = monthlyTotalCash;
    this.monthlyTotalAccountsReceivable = monthlyTotalAccountsReceivable;
    this.monthlyTotalSales = monthlyTotalSales;
    this.monthlyTotalAccountsPayable = monthlyTotalAccountsPayable;
    this.monthlyTotalCostOfSales = monthlyTotalCostOfSales;
    this.monthlyTotalInventory = monthlyTotalInventory;
    this.monthlyTotalOtherCurrentAssets = monthlyTotalOtherCurrentAssets;
    this.monthlyTotalOtherCurrentLiabilities = monthlyTotalOtherCurrentLiabilities;
    this.monthlyTotalMarketableSecurities = monthlyTotalMarketableSecurities;
    this.dateOrder = dateOrder;
  }

  get ascendantDates() {
    return this.dates.sort((a, b) => {
      if (a > b) return 1;
      if (a < b) return -1;
    });
  }

  get descendantDates() {
    return this.dates.sort((a, b) => {
      if (a > b) return -1;
      if (a < b) return 1;
    });
  }

  get orderedDates() {
    if (this.dateOrder === ASCENDANT_ORDER) {
      return this.ascendantDates;
    }

    return this.descendantDates;
  }

  get datesFormatted() {
    return this.orderedDates.map((date) => {
      return DateTime.fromISO(date).toFormat("LLLL y");
    });
  }

  get yearsFormatted() {
    return this.dateOrder === ASCENDANT_ORDER ? this.years.sort() : this.years.sort().reverse();
  }

  get yearlyTotalSales() {
    return this.calculateTotalByYear(this.monthlyTotalSales, { sum: true });
  }

  get yearlyTotalCostOfSales() {
    return this.calculateTotalByYear(this.monthlyTotalCostOfSales, { sum: true });
  }

  get yearlyTotalCash() {
    return this.calculateTotalByYear(this.monthlyTotalCash);
  }

  get yearlyTotalAccountsReceivable() {
    return this.calculateTotalByYear(this.monthlyTotalAccountsReceivable);
  }

  get yearlyTotalMarketableSecurities() {
    return this.calculateTotalByYear(this.monthlyTotalMarketableSecurities);
  }

  get yearlyTotalInventory() {
    return this.calculateTotalByYear(this.monthlyTotalInventory);
  }

  get yearlyTotalOtherCurrentAssets() {
    return this.calculateTotalByYear(this.monthlyTotalOtherCurrentAssets);
  }

  get yearlyTotalAccountsPayable() {
    return this.calculateTotalByYear(this.monthlyTotalAccountsPayable);
  }

  get yearlyTotalOtherCurrentLiabilities() {
    return this.calculateTotalByYear(this.monthlyTotalOtherCurrentLiabilities);
  }

  get yearlyWCCurrentAssets() {
    return this.calculateTotalByYear(this.wcCurrentAssets);
  }

  get yearlyWCCurrentLiabilities() {
    return this.calculateTotalByYear(this.wcCurrentLiabilities);
  }
  
  get monthlyTotalSalesHash() {
    const hash = GeneralHelper.convertArrayToKeyAndValueObject(
      this.monthlyTotalSales,
      "date",
      "value"
    );
    
    return this.formatDateAndValueHash(hash);
  }

  get monthlyTotalAccountsReceivableHash() {
    const hash = GeneralHelper.convertArrayToKeyAndValueObject(
      this.monthlyTotalAccountsReceivable,
      "date",
      "value"
    );

    return this.formatDateAndValueHash(hash);
  }

  get monthlyTotalAccountsPayableHash() {
    const hash = GeneralHelper.convertArrayToKeyAndValueObject(
      this.monthlyTotalAccountsPayable,
      "date",
      "value"
    );

    return this.formatDateAndValueHash(hash);
  }

  get monthlyCostOfSalesHash() {
    return GeneralHelper.convertArrayToKeyAndValueObject(
      this.monthlyTotalCostOfSales,
      "date",
      "value"
    );
  }

  get monthlyTotalInventoryHash() {
    const hash = GeneralHelper.convertArrayToKeyAndValueObject(
      this.monthlyTotalInventory,
      "date",
      "value"
    );

    return this.formatDateAndValueHash(hash);
  }

  get monthlyTotalOtherCurrentAssetsHash() {
    const hash = GeneralHelper.convertArrayToKeyAndValueObject(
      this.monthlyTotalOtherCurrentAssets,
      "date",
      "value"
    );

    return this.formatDateAndValueHash(hash);
  }

  get monthlyTotalOtherCurrentLiabilitiesHash() {
    const hash = GeneralHelper.convertArrayToKeyAndValueObject(
      this.monthlyTotalOtherCurrentLiabilities,
      "date",
      "value"
    );

    return this.formatDateAndValueHash(hash);
  }

  get wcCurrentAssetsHash() {
    const hash = GeneralHelper.convertArrayToKeyAndValueObject(
      this.wcCurrentAssets,
      "date",
      "value"
    );

    return this.formatDateAndValueHash(hash);
  }

  get wcCurrentLiabilitiesHash() {
    const hash = GeneralHelper.convertArrayToKeyAndValueObject(
      this.wcCurrentLiabilities,
      "date",
      "value"
    );

    return this.formatDateAndValueHash(hash);
  }

  get monthlyTotalMarketableSecuritiesHash() {
    const hash = GeneralHelper.convertArrayToKeyAndValueObject(
      this.monthlyTotalMarketableSecurities,
      "date",
      "value"
    );

    return this.formatDateAndValueHash(hash);
  }

  get cashNetWorkingCapitalHash() {
    const hash = GeneralHelper.convertArrayToKeyAndValueObject(
      this.cashNetWorkingCapital,
      "date",
      "value"
    );

    return this.formatDateAndValueHash(hash);
  }

  get nonCashNetWorkingCapitalHash() {
    const hash = GeneralHelper.convertArrayToKeyAndValueObject(
      this.nonCashNetWorkingCapital,
      "date",
      "value"
    );

    return this.formatDateAndValueHash(hash);
  }

  //wcCurrentAssets equals to the sum of 5 items below
  get wcCurrentAssets() {
    const {
      monthlyTotalCash,
      monthlyTotalAccountsReceivable,
      monthlyTotalMarketableSecurities,
      monthlyTotalInventory,
      monthlyTotalOtherCurrentAssets,
    } = this;
    return this.getArraySum(
      this.getArraySum(
        this.getArraySum(
          this.getArraySum(monthlyTotalCash, monthlyTotalAccountsReceivable),
          monthlyTotalMarketableSecurities
        ),
        monthlyTotalInventory
      ),
      monthlyTotalOtherCurrentAssets
    );
  }

  //wcCurrentLiabilities equals to the sum of 2 items below
  get wcCurrentLiabilities() {
    const { monthlyTotalAccountsPayable, monthlyTotalOtherCurrentLiabilities } =
      this;
    return this.getArraySum(
      monthlyTotalAccountsPayable,
      monthlyTotalOtherCurrentLiabilities
    );
  }

  get daysSalesOutstanding() {
    const { monthlyTotalAccountsReceivableHash, monthlyTotalSalesHash } = this;
    return this.getItems(
      "Days Items",
      3,
      monthlyTotalAccountsReceivableHash,
      monthlyTotalSalesHash
    );
  }

  get yearlyDaysSalesOutstanding() {
    const { yearlyTotalAccountsReceivable, yearlyTotalSales } = this;
    return this.getYearlyItems("Days Items", yearlyTotalAccountsReceivable, yearlyTotalSales);
  }

  get daysPayableOutstanding() {
    const { monthlyTotalAccountsPayableHash, monthlyCostOfSalesHash } = this;
    return this.getItems(
      "Days Items",
      3,
      monthlyTotalAccountsPayableHash,
      monthlyCostOfSalesHash
    );
  }

  get yearlyDaysPayableOutstanding() {
    const { yearlyTotalAccountsPayable, yearlyTotalCostOfSales } = this;
    return this.getYearlyItems("Days Items", yearlyTotalAccountsPayable, yearlyTotalCostOfSales);
  }

  get daysInventoryOnHand() {
    const { monthlyTotalInventoryHash, monthlyCostOfSalesHash } = this;
    return this.getItems(
      "Days Items",
      3,
      monthlyTotalInventoryHash,
      monthlyCostOfSalesHash
    );
  }

  get yearlyDaysInventoryOnHand() {
    const { yearlyTotalInventory, yearlyTotalCostOfSales } = this;
    return this.getYearlyItems("Days Items", yearlyTotalInventory, yearlyTotalCostOfSales);
  }

  get otherCurrentAssetsPercentageOfSales() {
    const { monthlyTotalOtherCurrentAssetsHash, monthlyTotalSalesHash } = this;
    return this.getItems(
      "Percent of Sales Items",
      3,
      monthlyTotalOtherCurrentAssetsHash,
      monthlyTotalSalesHash
    );
  }

  get yearlyOtherCurrentAssetsPercentageOfSales() {
    const { yearlyTotalOtherCurrentAssets, yearlyTotalSales } = this;
    return this.getYearlyItems("Percent of Sales Items", yearlyTotalOtherCurrentAssets, yearlyTotalSales);
  }

  get otherLiabilitiesPercentageOfSales() {
    const { monthlyTotalOtherCurrentLiabilitiesHash, monthlyTotalSalesHash } = this;
    return this.getItems(
      "Percent of Sales Items",
      3,
      monthlyTotalOtherCurrentLiabilitiesHash,
      monthlyTotalSalesHash
    );
  }

  get yearlyOtherLiabilitiesPercentageOfSales() {
    const { yearlyTotalOtherCurrentLiabilities, yearlyTotalSales } = this;
    return this.getYearlyItems("Percent of Sales Items", yearlyTotalOtherCurrentLiabilities, yearlyTotalSales);
  }

  get cashWorkingCapitalRatio() {
    const { wcCurrentAssetsHash, wcCurrentLiabilitiesHash } = this;
    return this.orderedDates.map((date) => {
      const currentMonthDate = DateTime.fromISO(date).toFormat("yyyy-LL-dd");
      let value =
        wcCurrentAssetsHash[currentMonthDate] /
        wcCurrentLiabilitiesHash[currentMonthDate];

      return new DateAndValue({
        date,
        value,
      });
    });
  }

  get yearlyCashWorkingCapitalRatio() {
    const { yearlyWCCurrentAssets, yearlyWCCurrentLiabilities } = this;
    return this.getYearlyItems("Multiple", yearlyWCCurrentAssets, yearlyWCCurrentLiabilities);
  }

  get nonCashWorkingCapitalRatio() {
    const {
      monthlyTotalAccountsReceivableHash,
      monthlyTotalMarketableSecuritiesHash,
      monthlyTotalInventoryHash,
      monthlyTotalOtherCurrentAssetsHash,
      wcCurrentLiabilitiesHash,
    } = this;
    return this.orderedDates.map((date) => {
      const currentMonthDate = DateTime.fromISO(date).toFormat("yyyy-LL-dd");
      let value =
        (monthlyTotalAccountsReceivableHash[currentMonthDate] +
          monthlyTotalMarketableSecuritiesHash[currentMonthDate] +
          monthlyTotalInventoryHash[currentMonthDate] +
          monthlyTotalOtherCurrentAssetsHash[currentMonthDate]) /
        wcCurrentLiabilitiesHash[currentMonthDate];

      return new DateAndValue({
        date,
        value,
      });
    });
  }

  get yearlyNonCashWorkingCapitalRatio() {
    const {
      yearlyTotalAccountsReceivable,
      yearlyTotalMarketableSecurities,
      yearlyTotalInventory,
      yearlyTotalOtherCurrentAssets,
      yearlyWCCurrentLiabilities,
    } = this;
    
    return this.getYearlyItems(
      "Multiple",
      this.getArraySum(
        this.getArraySum(yearlyTotalAccountsReceivable, yearlyTotalMarketableSecurities),
        this.getArraySum(yearlyTotalInventory, yearlyTotalOtherCurrentAssets)
      ),
      yearlyWCCurrentLiabilities
    );
  }

  get inventoryTurns() {
    const { monthlyCostOfSalesHash, monthlyTotalInventoryHash } = this;
    return this.getItems(
      "Inventory Turns",
      12,
      monthlyCostOfSalesHash,
      monthlyTotalInventoryHash
    );
  }

  get yearlyInventoryTurns() {
    const { yearlyTotalCostOfSales, yearlyTotalInventory } = this;
    return this.getYearlyItems("Inventory Turns", yearlyTotalCostOfSales, yearlyTotalInventory);
  }

  get cashConversionCycleDays() {
    const {
      daysSalesOutstanding,
      daysPayableOutstanding,
      daysInventoryOnHand,
    } = this;
    return daysSalesOutstanding.map((data, index) => {
      let date = data.date;
      let value = 0;
      let disabled = data.disabled;

      value =
        daysSalesOutstanding[index].value -
        daysPayableOutstanding[index].value +
        daysInventoryOnHand[index].value;

      return new DateAndValue({
        date,
        value,
        disabled,
      });
    });
  }

  get yearlyCashConversionCycleDays() {
    const {
      yearlyDaysSalesOutstanding,
      yearlyDaysPayableOutstanding,
      yearlyDaysInventoryOnHand
    } = this;
    return this.getArraySum(
      this.getArraySum(yearlyDaysSalesOutstanding, yearlyDaysInventoryOnHand),
      yearlyDaysPayableOutstanding,
      { minus: true }
    )
  }

  get cashNetWorkingCapital() {
    const { wcCurrentAssetsHash, wcCurrentLiabilitiesHash } = this;
    return this.orderedDates.map((date) => {
      const currentMonthDate = DateTime.fromISO(date).toFormat("yyyy-LL-dd");
      let value =
        wcCurrentAssetsHash[currentMonthDate] -
        wcCurrentLiabilitiesHash[currentMonthDate];

      return new DateAndValue({
        date,
        value,
      });
    });
  }

  get yearlyCashNetWorkingCapital() {
    const { yearlyWCCurrentAssets, yearlyWCCurrentLiabilities } = this;
    return this.getArraySum(yearlyWCCurrentAssets, yearlyWCCurrentLiabilities, { minus: true });
  }

  get cashNetWorkingCapitalTwelveMonthsTarget() {
    const { cashNetWorkingCapitalHash } = this;
    return this.getItems("Months Target", 12, cashNetWorkingCapitalHash);
  }

  get yearlyCashNetWorkingCapitalTwelveMonthsTarget() {
    const { cashNetWorkingCapitalTwelveMonthsTarget } = this;
    return this.calculateTotalByYear(cashNetWorkingCapitalTwelveMonthsTarget);
  }

  get cashNetWorkingCapitalSixMonthsTarget() {
    const { cashNetWorkingCapitalHash } = this;
    return this.getItems("Months Target", 6, cashNetWorkingCapitalHash);
  }

  get yearlyCashNetWorkingCapitalSixMonthsTarget() {
    const { cashNetWorkingCapitalSixMonthsTarget } = this;
    return this.calculateTotalByYear(cashNetWorkingCapitalSixMonthsTarget);
  }

  get cashNetWorkingCapitalThreeMonthsTarget() {
    const { cashNetWorkingCapitalHash } = this;
    return this.getItems("Months Target", 3, cashNetWorkingCapitalHash);
  }

  get yearlyCashNetWorkingCapitalThreeMonthsTarget() {
    const { cashNetWorkingCapitalThreeMonthsTarget } = this;
    return this.calculateTotalByYear(cashNetWorkingCapitalThreeMonthsTarget);
  }

  get nonCashNetWorkingCapital() {
    const {
      monthlyTotalAccountsReceivableHash,
      monthlyTotalMarketableSecuritiesHash,
      monthlyTotalInventoryHash,
      monthlyTotalOtherCurrentAssetsHash,
      wcCurrentLiabilitiesHash,
    } = this;
    return this.orderedDates.map((date) => {
      const currentMonthDate = DateTime.fromISO(date).toFormat("yyyy-LL-dd");
      let value =
        monthlyTotalAccountsReceivableHash[currentMonthDate] +
        monthlyTotalMarketableSecuritiesHash[currentMonthDate] +
        monthlyTotalInventoryHash[currentMonthDate] +
        monthlyTotalOtherCurrentAssetsHash[currentMonthDate] -
        wcCurrentLiabilitiesHash[currentMonthDate];

      return new DateAndValue({
        date,
        value,
      });
    });
  }

  get yearlyNonCashNetWorkingCapital() {
    const {
      yearlyTotalAccountsReceivable,
      yearlyTotalMarketableSecurities,
      yearlyTotalInventory,
      yearlyTotalOtherCurrentAssets,
      yearlyWCCurrentLiabilities
    } = this;
    return this.getArraySum(
      this.getArraySum(
        this.getArraySum(yearlyTotalAccountsReceivable, yearlyTotalMarketableSecurities),
        this.getArraySum(yearlyTotalInventory, yearlyTotalOtherCurrentAssets)
      ),
      yearlyWCCurrentLiabilities,
      { minus: true }
    )
  }

  get nonCashNetWorkingCapitalTwelveMonthsTarget() {
    const { nonCashNetWorkingCapitalHash } = this;
    return this.getItems("Months Target", 12, nonCashNetWorkingCapitalHash);
  }

  get yearlyNonCashNetWorkingCapitalTwelveMonthsTarget() {
    const { nonCashNetWorkingCapitalTwelveMonthsTarget } = this;
    return this.calculateTotalByYear(nonCashNetWorkingCapitalTwelveMonthsTarget);
  }

  get nonCashNetWorkingCapitalSixMonthsTarget() {
    const { nonCashNetWorkingCapitalHash } = this;
    return this.getItems("Months Target", 6, nonCashNetWorkingCapitalHash);
  }

  get yearlyNonCashNetWorkingCapitalSixMonthsTarget() {
    const { nonCashNetWorkingCapitalSixMonthsTarget } = this;
    return this.calculateTotalByYear(nonCashNetWorkingCapitalSixMonthsTarget);
  }

  get nonCashNetWorkingCapitalThreeMonthsTarget() {
    const { nonCashNetWorkingCapitalHash } = this;
    return this.getItems("Months Target", 3, nonCashNetWorkingCapitalHash);
  }

  get yearlyNonCashNetWorkingCapitalThreeMonthsTarget() {
    const { nonCashNetWorkingCapitalThreeMonthsTarget } = this;
    return this.calculateTotalByYear(nonCashNetWorkingCapitalThreeMonthsTarget);
  }

  getItems(
    itemType,
    numberOfMonths, //number of months used for every calculation
    numeratorHash,
    dividerHash = this.monthlyTotalSalesHash //We set default value because "Months Target" doesn't have divider hash
  ) {
    const result = this.ascendantDates.map((date, index) => {
      let value = 0;
      let disabled = true;

      if (index > numberOfMonths - 2) {
        //calculation for undisabled months

        let numerator = 0;
        let divider = 1;
        let multiplier = 1;
        let monthDateArray = []; //Month dates used for calculation
        for (let i = 0; i < numberOfMonths; i++) {
          monthDateArray[i] = DateTime.fromISO(
            this.ascendantDates[index - i] //Indices need to be calculated based on the ascentant order dates
          ).toFormat("yyyy-LL-dd");
        }

        if (itemType === "Days Items") {
          const days = [31, 28, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31]; //days of Jan to Dec
          numerator =
            this.getSumOfHash(numeratorHash, monthDateArray) / numberOfMonths;
          divider = this.getSumOfHash(dividerHash, monthDateArray);
          multiplier = monthDateArray.reduce(
            //total days of months
            (acc, monthDate) => acc + days[new Date(monthDate).getMonth()],
            0
          );
        } else if (itemType === "Percent of Sales Items") {
          numerator =
            this.getSumOfHash(numeratorHash, monthDateArray) / numberOfMonths;
          divider = dividerHash[monthDateArray[0]];
          multiplier = 100; //we must multiply 100 for percentage values
        } else if (itemType === "Inventory Turns") {
          numerator = this.getSumOfHash(numeratorHash, monthDateArray);
          divider =
            this.getSumOfHash(dividerHash, monthDateArray) / numberOfMonths;
        } else if (itemType === "Months Target") {
          numerator =
            this.getSumOfHash(numeratorHash, monthDateArray) / numberOfMonths;
        }

        value = (numerator / divider) * multiplier;
        disabled = false;
      }

      return new DateAndValue({
        date,
        value,
        disabled,
      });
    });

    return this.dateOrder === DESCENDANT_ORDER ? result.reverse() : result;
  }

  getYearlyItems(type, array1, array2) {
    let result = this.years.sort().map((year) => new DateAndValue({
      date: year,
      value: 0,
      disabled: false
    }));

    const ascArray1 = this.ascSort(array1);
    const ascArray2 = this.ascSort(array2);

    if(type === "Days Items") {
      result[0].disabled = true;
      result.map((obj, i) => {
        if(i > 0) result[i].value = (ascArray1[i].value + ascArray1[i - 1].value) / 2 / ascArray2[i].value * 365;
      })
    }
    else if(type === "Inventory Turns") {
      result[0].disabled = true;
      result.map((obj, i) => {
        if(i > 0) result[i].value = ascArray1[i].value / ((ascArray2[i].value + ascArray2[i - 1].value) / 2);
      })
    }
    else if(type === "Percent of Sales Items") result.map((obj, i) => { result[i].value = ascArray1[i].value / ascArray2[i].value * 100 });
    else if(type === "Multiple") result.map((obj, i) => { result[i].value = ascArray1[i].value / ascArray2[i].value });

    return this.getOrderedList(result);
  }

  getSumOfHash(hash, monthDateArray) {
    //get sum of results hash of every month
    return monthDateArray.reduce((acc, month) => acc + hash[month], 0);
  }

  getArraySum(array1, array2, option = { minus: false }) {
    //get the style of arrays, add element values of arrays
    let len1 = array1.length;
    let len2 = array2.length;
    let len; //length of result array
    let result = [];
    if (len2 < 1) return array1; //no need to calculate when array2 is empty

    len = len2;
    //if array1 is empty, fill elements with zero values
    if (len1 < 1) {
      for (let i = 0; i < len; i++)
        array1[i] = new DateAndValue({
          date: 0,
          value: 0,
          disabled: false,
        });
    }

    //add two array elements
    for (let i = 0; i < len; i++) {
      let value =
        option.minus ? array1[i].value - array2[i].value : array1[i].value + array2[i].value;
      result[i] = new DateAndValue({
        date: array2[i].date,
        value,
        disabled: array2[i].disabled,
      });
    }

    return result;
  }

  calculateTotalByYear(data, options = { sum: false, average: false }) {
    const yearTotalMap = {};
    let year = this.descendantDates[0];
    year = typeof year === "string" ? new Date(year) : year;
    data = this.ascSort(data).reverse();

    for (const item of data) {
      const value = item.value;

      if (!isNaN(value)) {
        if (!yearTotalMap[year.toLocaleDateString()]) {
          yearTotalMap[year.toLocaleDateString()] = {
            sum: 0,
            count: 0,
          };
        }
        if(options.sum || options.average || yearTotalMap[year.toLocaleDateString()].count === 0)
          yearTotalMap[year.toLocaleDateString()].sum += value;
          //we get the sum of 12 months if option.sum is true, otherwise, get the last month
        yearTotalMap[year.toLocaleDateString()].count++;
        if (yearTotalMap[year.toLocaleDateString()].count === 12) {
          if(options.average) yearTotalMap[year.toLocaleDateString()].sum /= yearTotalMap[year.toLocaleDateString()].count;
          year.setFullYear(year.getFullYear() - 1);
        }
      }
    }

    if(yearTotalMap[year.toLocaleDateString()]?.count && options.average)
      yearTotalMap[year.toLocaleDateString()].sum /= yearTotalMap[year.toLocaleDateString()].count;

    const result = Object.entries(yearTotalMap).map(
      ([year, { sum, count }]) => ({
        date: DateTime.fromJSDate(new Date(year)).toFormat("LLLL y"),
        value: sum,
      })
    );
    
    return this.getOrderedList(result);
  }

  getOrderedList(list) {
    if(list.length === 0) return this.orderedDates.map((date) => new DateAndValue({
      date: date,
      value: 0,
    }));  //we need it in order to avoid empty arrays

    if (this.dateOrder === ASCENDANT_ORDER) {
      return list.sort((a, b) => {
        if (a.date < b.date) return -1;
        if (a.date > b.date) return 1;
      });
    }

    return list.sort((a, b) => {
      if (a.date < b.date) return 1;
      if (a.date > b.date) return -1;
    });
  }

  ascSort(series) {
    //sort monthly time series by ascendant order
    return series.sort((a, b) => {
      if (a.date > b.date) return 1;
      if (a.date < b.date) return -1;
    });
  }

  formatDateAndValueHash(inputHash) {
    let hash;

    //in case there comes an empty array or array with empty object elements
    if (Object.keys(inputHash).length > 0 && Object.keys(inputHash)[0] != "0")
      hash = inputHash;
    else
      hash = GeneralHelper.convertArrayToKeyAndValueObject(
        this.dates.map((date) => ({ date, value: 0 })),
        "date",
        "value"
      );

    return Object.keys(hash).reduce((acc, key) => {
      const formattedKey = DateTime.fromISO(key).toFormat("yyyy-LL-01");
      acc[formattedKey] = hash[key];
      return acc;
    }, {});
  }
}
